/**
 * 학원 멤버 화면 — 학부모가 가입한 학원 안에서 보는 페이지
 * 학원이 켠 기능(features)에 따라 탭이 동적으로 노출된다.
 *  홈 · 출석 · 공지 · 자료실 · 일정
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  IoArrowBack, IoChatbubbleEllipsesOutline,
  IoMegaphoneOutline, IoCalendarOutline, IoDocumentTextOutline,
  IoDownloadOutline, IoChevronForward,
} from 'react-icons/io5';
import { useAcademy } from '../../context/AcademyContext';
import academyService from '../../services/academyService';
import AcademyPublicInfo from '../../components/academy/AcademyPublicInfo';
import AttendanceCalendar from '../../components/academy/AttendanceCalendar';
import BrandLoader from '../../components/common/BrandLoader';
import Badge from '../../components/common/Badge';

const fmtTs = (ts) => {
  if (!ts?.seconds) return '';
  const d = new Date(ts.seconds * 1000);
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}.${p(d.getMonth() + 1)}.${p(d.getDate())}`;
};

const fmtDate = (s) => {
  if (!s) return '';
  const [y, m, d] = String(s).split('-');
  return `${y}.${m}.${d}`;
};

const MemberAcademyPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { joinedAcademies, loading: acLoading } = useAcademy();

  const academy = joinedAcademies.find((a) => a.id === id) || null;
  const children = academy?.myChildren || [];

  const [tab, setTab] = useState('홈');
  const [notices, setNotices] = useState([]);
  const [classNotices, setClassNotices] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [events, setEvents] = useState([]);
  const [myAssignments, setMyAssignments] = useState([]);
  const [loading, setLoading] = useState(true);

  const kidKey = children.map((c) => c.id).join(',');

  useEffect(() => {
    if (!id) return undefined;
    let alive = true;
    (async () => {
      const [n, m, e] = await Promise.all([
        academyService.getAcademyNotices(id),
        academyService.getAcademyMaterials(id),
        academyService.getEvents(id),
      ]);
      if (!alive) return;
      setNotices(n); setMaterials(m); setEvents(e);
      setLoading(false);
    })();
    return () => { alive = false; };
  }, [id]);

  // 자녀가 속한 클래스들의 과제 + 클래스 공지 모으기
  useEffect(() => {
    if (!id || !kidKey) { setMyAssignments([]); setClassNotices([]); return undefined; }
    let alive = true;
    (async () => {
      const ids = kidKey.split(',');
      const classes = await academyService.getClasses(id);
      const myClasses = classes.filter((c) => ids.some((k) => (c.studentIds || []).includes(k)));
      const asgList = [];
      const nList = [];
      for (const c of myClasses) {
        // eslint-disable-next-line no-await-in-loop
        const [asgs, ns] = await Promise.all([
          academyService.getAssignments(id, c.id),
          academyService.getNotices(id, c.id),
        ]);
        asgs.forEach((a) => {
          ids.forEach((kidId) => {
            if ((c.studentIds || []).includes(kidId)) {
              const sub = (a.submissions || {})[kidId] || {};
              const kidName = children.find((cc) => cc.id === kidId)?.name || '';
              asgList.push({ asg: a, className: c.name, kidId, kidName, sub });
            }
          });
        });
        ns.forEach((n) => nList.push({ ...n, source: c.name }));
      }
      asgList.sort((a, b) => (b.asg.dueDate || '').localeCompare(a.asg.dueDate || ''));
      if (alive) { setMyAssignments(asgList); setClassNotices(nList); }
    })();
    return () => { alive = false; };
  }, [id, kidKey, children]);

  // 학원 공지 + 자녀 클래스 공지 합치기 (최신순)
  const allNotices = useMemo(() => {
    const a = (notices || []).map((n) => ({ ...n, source: '학원' }));
    const b = classNotices || [];
    return [...a, ...b].sort(
      (x, y) => (y.createdAt?.seconds || 0) - (x.createdAt?.seconds || 0),
    );
  }, [notices, classNotices]);

  if (acLoading) return <BrandLoader fullscreen />;

  if (!academy) {
    return (
      <Wrap>
        <TopBar>
          <BackBtn onClick={() => navigate('/app/my')}><IoArrowBack size={22} /></BackBtn>
          <TopTitle>내 학원</TopTitle>
        </TopBar>
        <Center>가입한 학원이 아니거나 찾을 수 없어요.</Center>
      </Wrap>
    );
  }

  const f = academy.features || {};
  const tabs = ['홈'];
  if (f.attendance !== false) tabs.push('출석');
  if (f.assignment !== false) tabs.push('과제');
  if (f.notice !== false) tabs.push('공지');
  if (f.material !== false) tabs.push('자료실');
  tabs.push('일정');

  return (
    <Wrap>
      <TopBar>
        <BackBtn onClick={() => navigate('/app/my')}><IoArrowBack size={22} /></BackBtn>
        <TopTitle>{academy.name}</TopTitle>
        <ChatIconBtn type="button" onClick={() => navigate(`/app/chat/${id}`)}>
          <IoChatbubbleEllipsesOutline size={22} />
        </ChatIconBtn>
      </TopBar>

      <Tabs>
        {tabs.map((t) => (
          <TabBtn key={t} type="button" $on={tab === t} onClick={() => setTab(t)}>{t}</TabBtn>
        ))}
      </Tabs>

      <Body>
        {loading ? (
          <Loading><BrandLoader /></Loading>
        ) : (
          <>
            {/* ── 홈 ── */}
            {tab === '홈' && (
              <>
                <Hero $url={academy.photoUrl}>
                  {!academy.photoUrl && <HeroLetter>{academy.name.slice(0, 1)}</HeroLetter>}
                </Hero>
                <AcademyName>{academy.name}</AcademyName>
                {children.length > 0 && (
                  <ChildRow>
                    {children.map((c) => <Badge key={c.id} $size="sm">{c.name}</Badge>)}
                    <ChildLabel>수강 중</ChildLabel>
                  </ChildRow>
                )}

                <ChatCard type="button" onClick={() => navigate(`/app/chat/${id}`)}>
                  <IoChatbubbleEllipsesOutline size={20} />
                  <ChatCardText>학원과 채팅 상담하기</ChatCardText>
                  <IoChevronForward size={18} color="#C2C5CE" />
                </ChatCard>

                {notices.length > 0 && (
                  <HomeBlock>
                    <BlockHead>
                      <IoMegaphoneOutline size={16} /> 최근 공지
                    </BlockHead>
                    {notices.slice(0, 2).map((n) => (
                      <MiniRow key={n.id} onClick={() => setTab('공지')}>
                        <MiniTitle>{n.title}</MiniTitle>
                        <MiniDate>{fmtTs(n.createdAt)}</MiniDate>
                      </MiniRow>
                    ))}
                  </HomeBlock>
                )}

                {events.length > 0 && (
                  <HomeBlock>
                    <BlockHead>
                      <IoCalendarOutline size={16} /> 학원 일정
                    </BlockHead>
                    {events.slice(0, 3).map((e) => (
                      <MiniRow key={e.id} onClick={() => setTab('일정')}>
                        <MiniTitle>{e.title}</MiniTitle>
                        <MiniDate>{fmtDate(e.date)}{e.time ? ` ${e.time}` : ''}</MiniDate>
                      </MiniRow>
                    ))}
                  </HomeBlock>
                )}

                <PublicWrap>
                  <AcademyPublicInfo academy={academy} />
                </PublicWrap>
              </>
            )}

            {/* ── 출석 ── */}
            {tab === '출석' && (
              children.length === 0 ? (
                <Empty>수강 중인 자녀가 없어요.</Empty>
              ) : (
                <AttendanceCalendar academyId={id} kids={children} />
              )
            )}

            {/* ── 공지 ── */}
            {tab === '과제' && (
              myAssignments.length === 0 ? (
                <Empty>등록된 과제가 없어요.</Empty>
              ) : (
                <List>
                  {myAssignments.map((m, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <AsgItem key={`${m.asg.id}-${m.kidId}`}>
                      <AsgItemHead>
                        <Badge $size="sm">
                          {m.className}{children.length > 1 ? ` · ${m.kidName}` : ''}
                        </Badge>
                        {m.asg.dueDate && <AsgDue>마감 {m.asg.dueDate}</AsgDue>}
                      </AsgItemHead>
                      <AsgItemTitle>{m.asg.title}</AsgItemTitle>
                      {m.asg.description && <AsgItemDesc>{m.asg.description}</AsgItemDesc>}
                      <AsgItemStatus>
                        {m.sub.submitted ? (
                          <StatLabel $kind="ok">제출 완료</StatLabel>
                        ) : (
                          <StatLabel $kind="warn">미제출</StatLabel>
                        )}
                      </AsgItemStatus>
                    </AsgItem>
                  ))}
                </List>
              )
            )}

            {tab === '공지' && (
              allNotices.length === 0 ? (
                <Empty>등록된 공지가 없어요.</Empty>
              ) : (
                <List>
                  {allNotices.map((n) => (
                    <NoticeCard key={`${n.source}-${n.id}`}>
                      <NoticeSource as="span"><Badge $size="sm">{n.source}</Badge></NoticeSource>
                      <NoticeTitle>{n.title}</NoticeTitle>
                      {n.content && <NoticeBody>{n.content}</NoticeBody>}
                      <NoticeDate>{fmtTs(n.createdAt)}</NoticeDate>
                    </NoticeCard>
                  ))}
                </List>
              )
            )}

            {/* ── 자료실 ── */}
            {tab === '자료실' && (
              materials.length === 0 ? (
                <Empty>등록된 자료가 없어요.</Empty>
              ) : (
                <List>
                  {materials.map((m) => (
                    <MatRow
                      key={m.id}
                      as={m.fileUrl ? 'a' : 'div'}
                      href={m.fileUrl || undefined}
                      target={m.fileUrl ? '_blank' : undefined}
                      rel={m.fileUrl ? 'noreferrer' : undefined}
                    >
                      <MatIcon><IoDocumentTextOutline size={20} /></MatIcon>
                      <MatInfo>
                        <MatTitle>{m.title || m.fileName}</MatTitle>
                        <MatDate>{fmtTs(m.createdAt)}</MatDate>
                      </MatInfo>
                      {m.fileUrl && <IoDownloadOutline size={20} color="#4A7CFE" />}
                    </MatRow>
                  ))}
                </List>
              )
            )}

            {/* ── 일정 ── */}
            {tab === '일정' && (
              events.length === 0 ? (
                <Empty>등록된 일정이 없어요.</Empty>
              ) : (
                <List>
                  {events.map((e) => (
                    <EventRow key={e.id}>
                      <EventDate>
                        <span>{fmtDate(e.date)}</span>
                        {e.time && <small>{e.time}</small>}
                      </EventDate>
                      <EventInfo>
                        <EventTitle>{e.title}</EventTitle>
                        {e.memo && <EventMemo>{e.memo}</EventMemo>}
                      </EventInfo>
                    </EventRow>
                  ))}
                </List>
              )
            )}
          </>
        )}
      </Body>
    </Wrap>
  );
};

export default MemberAcademyPage;

const Wrap = styled.div`
  min-height: 100vh;
  background: #fff;
`;

const TopBar = styled.div`
  height: 56px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.divider};
`;

const BackBtn = styled.button`
  background: none;
  display: flex;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const TopTitle = styled.p`
  flex: 1;
  font-size: 17px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ChatIconBtn = styled.button`
  background: none;
  display: flex;
  color: ${({ theme }) => theme.colors.primary};
`;

const Tabs = styled.div`
  display: flex;
  padding: 0 6px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.divider};
`;

const TabBtn = styled.button`
  flex: 1;
  padding: 12px 2px 13px;
  text-align: center;
  font-size: 14px;
  font-weight: 700;
  background: none;
  color: ${({ $on, theme }) => ($on ? theme.colors.primary : theme.colors.text.hint)};
  border-bottom: 2px solid ${({ $on, theme }) => ($on ? theme.colors.primary : 'transparent')};
`;

const Body = styled.div`
  max-width: 480px;
  margin: 0 auto;
  padding: 16px;
`;

const Loading = styled.div`
  padding: 60px 0;
`;

const Hero = styled.div`
  width: 100%;
  height: 170px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $url }) => ($url ? `#EDEFF3 url(${$url}) center/cover` : '#EDEFF3')};
`;

const HeroLetter = styled.span`
  font-size: 48px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.primary};
  opacity: 0.5;
`;

const AcademyName = styled.p`
  margin-top: 14px;
  font-size: 19px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ChildRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
`;

const ChildChip = styled.span`
  padding: 4px 11px;
  border-radius: 14px;
  font-size: 12.5px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => theme.colors.primaryLight};
`;

const ChildLabel = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.hint};
`;

const ChatCard = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 16px;
  padding: 14px 16px;
  border-radius: 14px;
  background: ${({ theme }) => theme.colors.primaryLight};
  color: ${({ theme }) => theme.colors.primary};
  text-align: left;
`;

const ChatCardText = styled.span`
  flex: 1;
  font-size: 14.5px;
  font-weight: 700;
`;

const HomeBlock = styled.div`
  margin-top: 22px;
`;

const BlockHead = styled.p`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 8px;
  svg { color: ${({ theme }) => theme.colors.primary}; }
`;

const MiniRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 2px;
  border-bottom: 1px solid #F0F1F4;
  cursor: pointer;
  &:last-child { border-bottom: none; }
`;

const MiniTitle = styled.span`
  flex: 1;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MiniDate = styled.span`
  flex-shrink: 0;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.hint};
`;

const PublicWrap = styled.div`
  margin-top: 24px;
  padding-top: 4px;
  border-top: 8px solid #F4F5F7;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
`;

const NoticeSource = styled.span`
  display: inline-block;
  margin-bottom: 6px;
  padding: 2px 8px;
  border-radius: 7px;
  font-size: 11px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => theme.colors.primaryLight};
`;

const NoticeCard = styled.div`
  padding: 16px 0;
  border-bottom: 1px solid #F0F1F4;
  &:last-child { border-bottom: none; }
`;

const NoticeTitle = styled.p`
  font-size: 15px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const NoticeBody = styled.p`
  margin-top: 6px;
  font-size: 13.5px;
  line-height: 1.6;
  white-space: pre-line;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const NoticeDate = styled.p`
  margin-top: 8px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.hint};
`;

const MatRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 2px;
  border-bottom: 1px solid #F0F1F4;
  text-decoration: none;
  &:last-child { border-bottom: none; }
`;

const MatIcon = styled.div`
  width: 42px;
  height: 42px;
  flex-shrink: 0;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => theme.colors.primaryLight};
`;

const MatInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const MatTitle = styled.p`
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MatDate = styled.p`
  margin-top: 3px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.hint};
`;

const EventRow = styled.div`
  display: flex;
  gap: 14px;
  padding: 14px 2px;
  border-bottom: 1px solid #F0F1F4;
  &:last-child { border-bottom: none; }
`;

const EventDate = styled.div`
  flex-shrink: 0;
  width: 78px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  span {
    font-size: 13px;
    font-weight: 700;
    color: ${({ theme }) => theme.colors.primary};
  }
  small {
    font-size: 11.5px;
    color: ${({ theme }) => theme.colors.text.hint};
  }
`;

const EventInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const EventTitle = styled.p`
  font-size: 14.5px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const EventMemo = styled.p`
  margin-top: 4px;
  font-size: 13px;
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Empty = styled.p`
  text-align: center;
  font-size: 13.5px;
  color: ${({ theme }) => theme.colors.text.hint};
  padding: 70px 0;
`;

const AsgItem = styled.div`
  padding: 14px 0;
  border-bottom: 1px solid #F0F1F4;
  &:last-child { border-bottom: none; }
`;

const AsgItemHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 6px;
`;

const AsgClass = styled.span`
  padding: 3px 9px;
  border-radius: 8px;
  font-size: 11.5px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => theme.colors.primaryLight};
`;

const AsgDue = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.hint};
`;

const AsgItemTitle = styled.p`
  font-size: 15.5px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const AsgItemDesc = styled.p`
  margin-top: 5px;
  font-size: 13px;
  line-height: 1.55;
  white-space: pre-line;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const AsgItemStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
`;

const ScoreBig = styled.span`
  font-size: 18px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.primary};
`;

const StatLabel = styled.span`
  padding: 4px 10px;
  border-radius: 11px;
  font-size: 12px;
  font-weight: 700;
  color: ${({ $kind }) => ($kind === 'warn' ? '#92400E' : '#1E40AF')};
  background: ${({ $kind }) => ($kind === 'warn' ? '#FEF3C7' : '#DBEAFE')};
`;

const AsgFeedback = styled.p`
  margin-top: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  background: #F4F5F7;
  font-size: 13px;
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Center = styled.p`
  text-align: center;
  padding: 80px 0;
  color: ${({ theme }) => theme.colors.text.hint};
`;
